import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Fingerprint, LogIn, Plus, Smartphone, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { usePublicConfig } from '../lib/auth';
import { queryClient } from '../lib/board';
import { deletePasskey, linkOidc, listAccounts, listPasskeys, passkeysSupported, registerPasskey } from '../lib/sso';
import { toast } from '../lib/toast';
import { Button } from './ui/ui';

const fmt = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' });

/** Verbundene Anmeldewege der Person (credential = Passwort, oidc = Anmeldedienst) */
export function useAccounts() {
  return useQuery({ queryKey: ['me', 'accounts'], queryFn: listAccounts });
}

function defaultName() {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac OS X/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows-PC';
  return 'Dieses Gerät';
}

export function SecuritySection() {
  const { data: cfg } = usePublicConfig();
  const { data: accounts } = useAccounts();
  const canPasskey = !!cfg?.login?.passkeys && passkeysSupported();
  const { data: passkeys } = useQuery({ queryKey: ['me', 'passkeys'], queryFn: listPasskeys, enabled: !!cfg?.login?.passkeys });
  const [busy, setBusy] = useState(false);
  const oidc = cfg?.login?.oidc;
  const linked = accounts?.includes('oidc');
  if (!oidc && !cfg?.login?.passkeys) return null;

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">Anmeldung</h2>

      {oidc && (
        <div className="flex items-center gap-3 rounded-xl border border-[var(--line)] p-3">
          <LogIn size={18} className="shrink-0 text-[var(--ink-soft)]" aria-hidden />
          <div className="min-w-0 flex-1 text-sm">
            <p className="font-medium">Single Sign-on</p>
            <p className="text-xs text-[var(--ink-soft)]">
              {linked ? `Verbunden – „${oidc.label}“ meldet dich an.` : 'Noch nicht verbunden. Danach reicht ein Klick auf der Anmeldeseite.'}
            </p>
          </div>
          {!linked && accounts && (
            <Button onClick={() => void linkOidc().catch((e: Error) => toast.error(e.message))}>Verbinden</Button>
          )}
        </div>
      )}

      {cfg?.login?.passkeys && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-[var(--ink-soft)]">
            Mit einem Passkey meldest du dich per Fingerabdruck, Gesichtserkennung oder Geräte-PIN an – ohne Passwort. Er wird auf deinem Gerät bzw.
            im Passwort-Manager gespeichert.
          </p>
          {!!passkeys?.length && (
            <ul className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">
              {passkeys.map((p) => (
                <li key={p.id} className="flex items-center gap-3 px-3 py-2">
                  <Fingerprint size={18} className="shrink-0 text-[var(--ink-soft)]" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{p.name || 'Passkey'}</p>
                    <p className="truncate text-xs text-[var(--ink-soft)]">
                      {p.createdAt ? `angelegt am ${fmt.format(new Date(p.createdAt))}` : ''}
                      {p.backedUp ? ' · auf mehreren Geräten verfügbar' : ''}
                    </p>
                  </div>
                  <button
                    aria-label={`Passkey „${p.name || 'Passkey'}“ entfernen`}
                    className="inline-flex size-10 items-center justify-center rounded-md text-[var(--ink-soft)] hover:text-rose-600"
                    onClick={async () => {
                      if (!window.confirm(`Passkey „${p.name || 'Passkey'}“ entfernen? Mit diesem Gerät meldest du dich danach wieder anders an.`)) return;
                      await deletePasskey(p.id);
                      void queryClient.invalidateQueries({ queryKey: ['me', 'passkeys'] });
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {canPasskey ? (
            <Button
              className="self-start"
              disabled={busy}
              onClick={async () => {
                const name = window.prompt('Name für diesen Passkey', defaultName());
                if (name === null) return;
                setBusy(true);
                try {
                  if (await registerPasskey(name.trim() || defaultName())) {
                    toast.info('Passkey angelegt. Ab jetzt kannst du dich damit anmelden.');
                    void queryClient.invalidateQueries({ queryKey: ['me', 'passkeys'] });
                  }
                } catch (err) {
                  toast.error((err as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Plus size={16} /> Passkey hinzufügen
            </Button>
          ) : (
            <p className="text-xs text-[var(--ink-soft)]">Dieser Browser unterstützt keine Passkeys.</p>
          )}
        </div>
      )}
    </section>
  );
}

interface AppSession {
  id: string;
  device: string;
  createdAt: string;
  lastSeenAt: string;
}

/** Angemeldete KaBa-Apps (0.56): sehen und einzeln abmelden – z. B. bei verlorenem Handy */
export function AppSessionsSection() {
  const { data: sessions } = useQuery({ queryKey: ['me', 'app-sessions'], queryFn: () => api.get<AppSession[]>('/api/mobile/sessions') });
  if (!sessions?.length) return null;
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">Angemeldete Apps</h2>
      <p className="text-sm text-[var(--ink-soft)]">Diese Geräte nutzen dein Konto über die KaBa-App. Abmelden beendet dort sofort den Zugriff.</p>
      <ul className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">
        {sessions.map((s) => (
          <li key={s.id} className="flex items-center gap-3 px-3 py-2">
            <Smartphone size={18} className="shrink-0 text-[var(--ink-soft)]" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{s.device}</p>
              <p className="text-xs text-[var(--ink-soft)]">
                Verbunden {fmt.format(new Date(s.createdAt))} · zuletzt aktiv {fmt.format(new Date(s.lastSeenAt))}
              </p>
            </div>
            <Button
              size="sm"
              onClick={async () => {
                if (!window.confirm(`„${s.device}“ abmelden? Die App auf diesem Gerät muss sich danach neu anmelden.`)) return;
                try {
                  await api.del(`/api/mobile/sessions/${s.id}`);
                  toast.info('Die App wurde abgemeldet.');
                } catch (err) {
                  toast.error((err as Error).message);
                }
                void queryClient.invalidateQueries({ queryKey: ['me', 'app-sessions'] });
              }}
            >
              Abmelden
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
